import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    WarningOutlined,
    DownloadOutlined,
    ArrowLeftOutlined,
    LoadingOutlined,
    FileExcelOutlined,
    SyncOutlined,
    ExportOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { App, Tooltip as AntdTooltip, Select, Modal, InputNumber } from "antd";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    AreaChart,
    Area,
} from "recharts";
import {
    fetchHybridSimulationConfig,
    downloadHybridReport,
    fetchSimulations,
    createHybridSimulation,
    runHybridSimulation,
    downloadHybridSimulationCSV,
} from "../../services/bessApi";
import { formatRowsForExcel, applyExcelNumberFormat } from "../../utils/downloadUtils";
import BessSizingResultPage from "../bess-sizing/BessSizingResultPage";

const TREND_FIELD_CONFIG = [
    { key: "solar_gen_33kv_mw", label: "Solar Gen at 33kV", color: "#f59e0b" },
    { key: "solar_to_bess_power", label: "Solar Power for BESS Charge", color: "#2563eb" },
    { key: "solar_power_at_poi", label: "Solar Power at POI", color: "#10b981" },
    { key: "grid_import_at_poi", label: "Grid Power at POI", color: "#ef4444" },
    { key: "bess_power_at_poi", label: "BESS Power at POI", color: "#8b5cf6" },
];

const WIND_TREND_FIELD_CONFIG = [
    { key: "solar_gen_33kv_mw", label: "Solar Gen at 33kV", color: "#f59e0b" },
    { key: "wind_gen_33kv_mw", label: "Wind Gen at 33kV", color: "#06b6d4" },
    { key: "solar_to_bess_power", label: "Solar Power for BESS Charge", color: "#2563eb" },
    { key: "solar_power_at_poi", label: "Solar Power at POI", color: "#10b981" },
    { key: "grid_import_at_poi", label: "Grid Power at POI", color: "#ef4444" },
    { key: "bess_power_at_poi", label: "BESS Power at POI", color: "#8b5cf6" },
];

const MONTH_OPTIONS = [
    { value: "1", label: "January (Month 1)" },
    { value: "2", label: "February (Month 2)" },
    { value: "3", label: "March (Month 3)" },
    { value: "4", label: "April (Month 4)" },
    { value: "5", label: "May (Month 5)" },
    { value: "6", label: "June (Month 6)" },
    { value: "7", label: "July (Month 7)" },
    { value: "8", label: "August (Month 8)" },
    { value: "9", label: "September (Month 9)" },
    { value: "10", label: "October (Month 10)" },
    { value: "11", label: "November (Month 11)" },
    { value: "12", label: "December (Month 12)" },
];

const aggregateMonthlyToYearly = (data) => {
    const yearsMap = {};
    data.forEach((row) => {
        const yr = Math.round(row.year);
        if (!yearsMap[yr]) {
            yearsMap[yr] = {
                year: yr,
                count: 0,
                ambient_temp_c: 0,
                solar_gen_33kv_mw: 0,
                wind_gen_33kv_mw: 0,
                solar_aux_power: 0,
                solar_power_at_33kv: 0,
                solar_power_after_dt: 0,
                aux_standby_mw: 0,
                aux_operation_mw: 0,
                solar_power_after_aux: 0,
                bess_capacity: 0,
                total_container_no: 0,
                solar_set_point: 0,
                export_solar_power: 0,
                solar_to_bess_power: 0,
                solar_power_at_poi: 0,
                grid_import_at_poi: 0,
                bess_power_at_poi: 0,
                bess_charge_after_losses: 0,
                avail_feeder_capicity: 0,
                short_fall_power: 0,
                grid_import_for_bess_charging: 0,
                remaining_short_fall_power: 0,
                total_bess_after_loss: 0,
                grid_import_at_33kv_bus: 0,
                grid_import_at_220kv_bus: 0,
                grid_import_at_400kv_bus: 0,
                bess_power: 0,
                bess_power_after_aux: 0,
                bess_power_at_33kv_bus: 0,
                bess_power_at_220kv_bus: 0,
                bess_power_at_400kv_bus: 0,
            };
        }
        const yData = yearsMap[yr];
        yData.count += 1;
        yData.ambient_temp_c += row.ambient_temp_c || 0;
        yData.solar_gen_33kv_mw += row.solar_gen_33kv_mw || 0;
        yData.wind_gen_33kv_mw += row.wind_gen_33kv_mw || row.wind_gen_at_wtg_mw || row.wind_gen_at_33kv_pss_mw || row.wind_gen_at_33kv_uss_mw || 0;
        yData.solar_aux_power += row.solar_aux_power || 0;
        yData.solar_power_at_33kv += row.solar_power_at_33kv || 0;
        yData.solar_power_after_dt += row.solar_power_after_dt || 0;
        yData.aux_standby_mw += row.aux_standby_mw || 0;
        yData.aux_operation_mw += row.aux_operation_mw || 0;
        yData.solar_power_after_aux += row.solar_power_after_aux || 0;
        yData.bess_capacity = Math.max(yData.bess_capacity, row.bess_capacity || 0);
        yData.total_container_no = Math.max(yData.total_container_no, row.total_container_no || 0);
        yData.solar_set_point += row.solar_set_point || 0;
        yData.export_solar_power += row.export_solar_power || 0;
        yData.solar_to_bess_power += row.solar_to_bess_power || row.power_for_bess_charging_at_33kv_bus || 0;
        yData.solar_power_at_poi += row.solar_power_at_poi !== undefined ? (row.solar_power_at_poi || 0) : (row.export_solar_power || row.solar_power_at_400kv_bus || 0);
        yData.grid_import_at_poi += row.grid_import_at_poi !== undefined ? (row.grid_import_at_poi || 0) : (row.grid_import_at_400kv_bus || row.grid_import_for_bess_charging || 0);
        yData.bess_power_at_poi += row.bess_power_at_poi !== undefined ? (row.bess_power_at_poi || 0) : (row.bess_power_at_400kv_bus || row.bess_power_at_220kv_bus || 0);
        yData.bess_charge_after_losses += row.bess_charge_after_losses || 0;
        yData.avail_feeder_capicity += row.avail_feeder_capicity || 0;
        yData.short_fall_power += row.short_fall_power || 0;
        yData.grid_import_for_bess_charging += row.grid_import_for_bess_charging || 0;
        yData.remaining_short_fall_power += row.remaining_short_fall_power || 0;
        yData.total_bess_after_loss += row.total_bess_after_loss || 0;
        yData.grid_import_at_33kv_bus += row.grid_import_at_33kv_bus || 0;
        yData.grid_import_at_220kv_bus += row.grid_import_at_220kv_bus || 0;
        yData.grid_import_at_400kv_bus += row.grid_import_at_400kv_bus || 0;
        yData.bess_power += row.bess_power || 0;
        yData.bess_power_after_aux += row.bess_power_after_aux || 0;
        yData.bess_power_at_33kv_bus += row.bess_power_at_33kv_bus || 0;
        yData.bess_power_at_220kv_bus += row.bess_power_at_220kv_bus || 0;
        yData.bess_power_at_400kv_bus += row.bess_power_at_400kv_bus || 0;
    });

    return Object.values(yearsMap).map((yData) => {
        const cnt = yData.count || 1;
        return {
            ...yData,
            ambient_temp_c: yData.ambient_temp_c / cnt,
            solar_set_point: yData.solar_set_point / cnt,
        };
    });
};

const prepareHeatmapData = (data) => {
    const yearsMap = {};
    data.forEach((row) => {
        const yr = Math.round(row.year);
        const m = Math.round(row.month);
        if (!yearsMap[yr]) {
            yearsMap[yr] = {
                year: yr,
                monthlyRows: {},
                totalSolarCharging: 0,
                totalGridCharging: 0,
                maxBessCapacity: 0,
                count: 0,
            };
        }
        const yData = yearsMap[yr];
        yData.monthlyRows[m] = row;
        yData.totalSolarCharging += row.solar_to_bess_power || 0;
        yData.totalGridCharging += row.grid_import_for_bess_charging || 0;
        yData.maxBessCapacity = Math.max(yData.maxBessCapacity, row.bess_capacity || 0);
        yData.count += 1;
    });

    return Object.values(yearsMap).map((yData) => {
        const totalCharging = yData.totalSolarCharging + yData.totalGridCharging;
        const gridImportPct = totalCharging > 0 ? (yData.totalGridCharging / totalCharging) * 100 : 0;
        const solarChargingPct = totalCharging > 0 ? (yData.totalSolarCharging / totalCharging) * 100 : 100;

        const months = [];
        for (let m = 1; m <= 12; m++) {
            const row = yData.monthlyRows[m];
            if (row) {
                const solar = row.solar_to_bess_power || 0;
                const grid = row.grid_import_for_bess_charging || 0;
                const tot = solar + grid;
                const solarPct = tot > 0 ? (solar / tot) * 100 : null;
                const status = (row.remaining_short_fall_power || 0) < 1.0 ? 'OK' : 'Not';
                months.push({
                    monthNum: m,
                    monthName: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1],
                    solarPct,
                    solarMwh: solar,
                    gridMwh: grid,
                    status,
                    shortfallMwh: row.remaining_short_fall_power || 0,
                });
            } else {
                months.push({
                    monthNum: m,
                    monthName: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1],
                    solarPct: null,
                    solarMwh: 0,
                    gridMwh: 0,
                    status: 'OK',
                    shortfallMwh: 0,
                });
            }
        }

        return {
            year: yData.year,
            gridImportPct,
            solarChargingPct,
            bessCapacity: yData.maxBessCapacity,
            months,
        };
    });
};

export default function HybridBessResultPage({ id: propId, backPath }) {
    const params = useParams();
    const id = propId || params.id;
    const navigate = useNavigate();
    const location = useLocation();
    const { message } = App.useApp();
    const resolvedBackPath = backPath || (location.pathname.includes("hybrid-bess-wind") ? "/hybrid-bess-wind" : "/hybrid-bess");

    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [monthlyData, setMonthlyData] = useState([]);
    const [bessSimulations, setBessSimulations] = useState([]);
    const [generatingReport, setGeneratingReport] = useState(false);
    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [downloadOption, setDownloadOption] = useState("all");
    const [downloadingMonthlyCSV, setDownloadingMonthlyCSV] = useState(false);
    const [monthlyModalVisible, setMonthlyModalVisible] = useState(false);
    const [monthlyYear, setMonthlyYear] = useState("1");
    const [monthlyMonth, setMonthlyMonth] = useState("1");

    // Interactive Min BESS Stored Energy Panel State
    const [editableYearlyMinBess, setEditableYearlyMinBess] = useState([]);
    const [updatingMinBess, setUpdatingMinBess] = useState(false);
    const [bulkMinBessVal, setBulkMinBessVal] = useState("");

    // Generation & Dispatch Trends Field Selector State
    const [selectedTrendFields, setSelectedTrendFields] = useState([
        "solar_gen_33kv_mw",
        "solar_to_bess_power",
        "solar_power_at_poi",
        "grid_import_at_poi",
        "bess_power_at_poi",
    ]);

    // BESS Charging Breakdown Field Selector State
    const [selectedChargingFields, setSelectedChargingFields] = useState([
        "solar_gen_33kv_mw",
        "solar_to_bess_power",
        "solar_power_at_poi",
        "grid_import_at_poi",
        "bess_power_at_poi",
    ]);

    const yearlyData = aggregateMonthlyToYearly(monthlyData);
    const heatmapData = prepareHeatmapData(monthlyData);

    const loadData = async () => {
        try {
            setLoading(true);
            const [configData, reportData, bessSims] = await Promise.all([
                fetchHybridSimulationConfig(id),
                downloadHybridReport(id),
                fetchSimulations(),
            ]);

            setConfig(configData);
            setMonthlyData(reportData.hourly_profile || []);
            setBessSimulations(bessSims || []);

            const isWind = configData.simulation_type === 2 || configData.simulation_type === "2";
            if (isWind) {
                setSelectedTrendFields([
                    "solar_gen_33kv_mw",
                    "wind_gen_33kv_mw",
                    "solar_to_bess_power",
                    "solar_power_at_poi",
                    "grid_import_at_poi",
                    "bess_power_at_poi",
                ]);
                setSelectedChargingFields([
                    "solar_gen_33kv_mw",
                    "wind_gen_33kv_mw",
                    "solar_to_bess_power",
                    "solar_power_at_poi",
                    "grid_import_at_poi",
                    "bess_power_at_poi",
                ]);
            } else {
                setSelectedTrendFields([
                    "solar_gen_33kv_mw",
                    "solar_to_bess_power",
                    "solar_power_at_poi",
                    "grid_import_at_poi",
                    "bess_power_at_poi",
                ]);
                setSelectedChargingFields([
                    "solar_gen_33kv_mw",
                    "solar_to_bess_power",
                    "solar_power_at_poi",
                    "grid_import_at_poi",
                    "bess_power_at_poi",
                ]);
            }

            const tenure = configData.simulation_tenure || 25;
            const defaultMinPct = configData.min_bess_store_energy !== null && configData.min_bess_store_energy !== undefined
                ? Number(configData.min_bess_store_energy)
                : 100;

            let yearlyList = [];
            if (Array.isArray(configData.min_bess_store_energy_yearly) && configData.min_bess_store_energy_yearly.length > 0) {
                yearlyList = configData.min_bess_store_energy_yearly.map(item => ({
                    year: Number(item.year),
                    pct: Number(item.pct ?? defaultMinPct)
                }));
            } else {
                for (let yr = 1; yr <= tenure; yr++) {
                    yearlyList.push({ year: yr, pct: defaultMinPct });
                }
            }
            setEditableYearlyMinBess(yearlyList);
        } catch (err) {
            console.error("Failed to load hybrid simulation results:", err);
            message.error(err.message || "Failed to load simulation results.");
        } finally {
            setLoading(false);
        }
    };

    const handleBulkApplyMinBess = (val) => {
        if (val === "" || val === null || val === undefined || isNaN(Number(val))) return;
        const numVal = Math.min(100, Math.max(0, Number(val)));
        setEditableYearlyMinBess(prev => prev.map(item => ({ ...item, pct: numVal })));
        message.success(`Set Min BESS Stored Energy target to ${numVal}% for all years.`);
    };

    const handleUpdateMinBessAndReRun = async () => {
        if (editableYearlyMinBess.length === 0) return;
        setUpdatingMinBess(true);
        try {
            const payload = {
                min_bess_store_energy_yearly: editableYearlyMinBess.map(item => ({
                    year: Number(item.year),
                    pct: Number(item.pct)
                }))
            };

            message.loading({ content: "Updating targets and re-running hybrid simulation... Please wait.", key: "rerun-hybrid", duration: 0 });
            await createHybridSimulation(payload, id);
            await runHybridSimulation(id);

            message.success({ content: "Hybrid simulation re-run completed successfully!", key: "rerun-hybrid" });
            await loadData();
        } catch (err) {
            console.error("Failed to re-run hybrid simulation:", err);
            message.error({ content: err.message || "Failed to re-run hybrid simulation.", key: "rerun-hybrid" });
        } finally {
            setUpdatingMinBess(false);
        }
    };

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const handleOpenDownloadModal = () => {
        setDownloadOption("all");
        setDownloadModalVisible(true);
    };

    const handleDownloadCSV = async () => {
        try {
            setDownloadModalVisible(false);
            setDownloadingCSV(true);
            message.loading({ content: "Downloading Yearly Report...", key: "download-yearly-csv", duration: 0 });
            
            const startYear = downloadOption === "all" ? 1 : parseInt(downloadOption, 10);
            const numYears = downloadOption === "all" ? (config?.simulation_tenure || 25) : 1;

            const blob = await downloadHybridSimulationCSV(id, startYear, numYears);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cleanSimName = config?.name ? config.name.trim().replace(/[^a-zA-Z0-9_-]/g, '_') : 'Unnamed';
            const yearSuffix = downloadOption === "all" ? "All_Years" : `Year_${downloadOption}`;
            a.download = `Hybrid_BESS_Simulation_${cleanSimName}_${id}_${yearSuffix}_output.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            
            message.success({ content: "Yearly Report downloaded successfully!", key: "download-yearly-csv" });
        } catch (err) {
            console.error(err);
            message.error({ content: "Failed to download Yearly Report: " + err.message, key: "download-yearly-csv" });
        } finally {
            setDownloadingCSV(false);
        }
    };

    const handleOpenMonthlyModal = () => {
        setMonthlyYear("1");
        setMonthlyMonth("1");
        setMonthlyModalVisible(true);
    };

    const handleDownloadMonthlyCSV = async () => {
        try {
            setMonthlyModalVisible(false);
            setDownloadingMonthlyCSV(true);
            message.loading({ content: "Downloading Monthly Report...", key: "download-monthly-csv", duration: 0 });
            
            const yearNum = parseInt(monthlyYear, 10) || 1;
            const monthNum = parseInt(monthlyMonth, 10) || 1;

            const blob = await downloadHybridSimulationCSV(id, yearNum, 1, monthNum);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cleanSimName = config?.name ? config.name.trim().replace(/[^a-zA-Z0-9_-]/g, '_') : 'Unnamed';
            a.download = `Hybrid_BESS_Simulation_${cleanSimName}_${id}_Year_${yearNum}_Month_${monthNum}_output.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            
            message.success({ content: "Monthly Report downloaded successfully!", key: "download-monthly-csv" });
        } catch (err) {
            console.error(err);
            message.error({ content: "Failed to download Monthly Report: " + err.message, key: "download-monthly-csv" });
        } finally {
            setDownloadingMonthlyCSV(false);
        }
    };

    const handleDownloadReport = async () => {
        setGeneratingReport(true);
        try {
            const pdf = new jsPDF("p", "mm", "a4");
            const pageIds = ["hybrid-report-page-1", "hybrid-report-page-2", "hybrid-report-page-3"];
            if (config?.bess_sizing_simulation) {
                pageIds.push("bess-report-page-1", "bess-report-page-2", "bess-report-page-3", "bess-report-page-4", "bess-report-page-5");
            }

            for (let i = 0; i < pageIds.length; i++) {
                const pageEl = document.getElementById(pageIds[i]);
                if (!pageEl) continue;

                const canvas = await html2canvas(pageEl, {
                    scale: 2.2,
                    useCORS: true,
                    logging: false,
                    scrollY: 0,
                    scrollX: 0,
                });

                const imgData = canvas.toDataURL("image/png");
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

                if (i < pageIds.length - 1) {
                    pdf.addPage();
                }
            }

            const cleanSimName = config?.name ? config.name.trim().replace(/[^a-zA-Z0-9_-]/g, '_') : 'Unnamed';

            pdf.save(`Hybrid_BESS_Simulation_${cleanSimName}_${id}_Report.pdf`);
            message.success("PDF report generated and downloaded successfully!");
        } catch (err) {
            console.error("Hybrid Simulation Report PDF Generation failed:", err);
            message.error("Failed to generate PDF report.");
        } finally {
            setGeneratingReport(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col justify-center items-center bg-[#f7f9fb] text-slate-600">
                <LoadingOutlined style={{ fontSize: 32 }} className="text-[#0052cc] mb-4" />
                <span className="text-sm font-semibold">Loading Simulation results...</span>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="min-h-screen flex flex-col justify-center items-center bg-[#f7f9fb] text-slate-600">
                <WarningOutlined style={{ fontSize: 32 }} className="text-red-500 mb-4" />
                <span className="text-sm font-semibold mb-4">Simulation results not found.</span>
                <button
                    type="button"
                    onClick={() => navigate(resolvedBackPath)}
                    className="h-10 px-5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                >
                    <ArrowLeftOutlined /> Back to Hybrid Builder
                </button>
            </div>
        );
    }

    const isWindSim = config.simulation_type === 2 || config.simulation_type === "2";
    const typeLabel = isWindSim
        ? "Solar + Wind + BESS"
        : "Solar + BESS";
    const currentTrendFieldConfig = isWindSim ? WIND_TREND_FIELD_CONFIG : TREND_FIELD_CONFIG;

    const matchedSizingSim = bessSimulations.find(s => s.id === parseInt(config.bess_sizing_simulation));
    const startYear = matchedSizingSim && matchedSizingSim.start_year ? parseInt(matchedSizingSim.start_year) : 2025;
    const sizingSimLabel = matchedSizingSim
        ? (matchedSizingSim.name || "Sizing Sim")
        : (config.bess_sizing_simulation_name || `Simulation ${config.bess_sizing_simulation}`);

    const hybridSimName = (config.name ? config.name.replace(/_hybrid$/i, "") : null) || matchedSizingSim?.name || config.bess_sizing_simulation_name || (config.bess_sizing_simulation ? `Simulation #${config.bess_sizing_simulation}` : `Simulation #${id}`);

    // Losses & Equipment Values
    const htAcCableLoss = config.ht_ac_cable_loss ?? matchedSizingSim?.ht_ac_cable_loss;
    const ltAcCableLoss = config.lt_ac_cable_loss ?? matchedSizingSim?.lt_ac_cable_loss;
    const ltDcCableLoss = config.lt_dc_cable_loss ?? matchedSizingSim?.lt_dc_cable_loss;
    const pcsLoss = config.pcs_loss ?? matchedSizingSim?.pcs_loss;
    const idtLoss = config.idt_loss ?? matchedSizingSim?.idt_loss;

    // Transformers
    const t220Loss = config.transformer_220kv_loss ?? config.transformer_220_loss ?? matchedSizingSim?.transformer_220kv_loss ?? matchedSizingSim?.transformer_220_loss;
    const t400Loss = config.transformer_400kv_loss ?? config.transformer_400_220_loss ?? config.transformer_400_loss ?? matchedSizingSim?.transformer_400kv_loss;

    // Transmission Lines (220kV / 400kV)
    const lineLoss220Direct = config.transmission_line_220kv_loss ?? config.transmission_line_loss ?? matchedSizingSim?.transmission_line_220kv_loss ?? matchedSizingSim?.transmission_line_loss;
    const res220 = config.conductor_resistance_220kv_ohm ?? config.conductor_resistance_ohm ?? matchedSizingSim?.conductor_resistance_220kv_ohm ?? matchedSizingSim?.conductor_resistance_ohm;
    const dist220 = config.transmission_line_220kv_distance_km ?? config.transmission_line_distance_km ?? matchedSizingSim?.transmission_line_220kv_distance_km ?? matchedSizingSim?.transmission_line_distance_km;

    const lineLoss400Direct = config.transmission_line_400kv_loss ?? matchedSizingSim?.transmission_line_400kv_loss;
    const res400 = config.conductor_resistance_400kv_ohm ?? matchedSizingSim?.conductor_resistance_400kv_ohm;
    const dist400 = config.transmission_line_400kv_distance_km ?? matchedSizingSim?.transmission_line_400kv_distance_km;

    const systemLossCards = [
        { label: "Solar Availability at 33kV", value: `${parseFloat(config.dt_33kv_pct || 0).toFixed(2)}%` },
        { label: "Grid Availability", value: `${parseFloat(config.grid_unavailability ?? config.plant_unavailability ?? 0).toFixed(2)}%` },
        { label: "BESS Availability", value: `${parseFloat(config.bess_unavailability ?? matchedSizingSim?.bess_plant_unavailability ?? matchedSizingSim?.bess_unavailability ?? 0).toFixed(2)}%` },
        htAcCableLoss != null && htAcCableLoss !== "" ? { label: "HT AC Cable Loss", value: `${parseFloat(htAcCableLoss).toFixed(2)}%` } : null,
        idtLoss != null && idtLoss !== "" ? { label: "IDT Loss", value: `${parseFloat(idtLoss).toFixed(2)}%` } : null,
        ltAcCableLoss != null && ltAcCableLoss !== "" ? { label: "LT AC Cable Loss", value: `${parseFloat(ltAcCableLoss).toFixed(2)}%` } : null,
        pcsLoss != null && pcsLoss !== "" ? { label: "PCS Loss", value: `${parseFloat(pcsLoss).toFixed(2)}%` } : null,
        ltDcCableLoss != null && ltDcCableLoss !== "" ? { label: "LT DC Cable Loss", value: `${parseFloat(ltDcCableLoss).toFixed(2)}%` } : null,
        t220Loss != null && t220Loss !== "" && Number(t220Loss) > 0 ? { label: "220/33 kV Transformer Loss", value: `${parseFloat(t220Loss).toFixed(2)}%` } : null,
        t400Loss != null && t400Loss !== "" && Number(t400Loss) > 0 ? { label: "400/220 kV Transformer Loss", value: `${parseFloat(t400Loss).toFixed(2)}%` } : null,
        lineLoss220Direct != null && lineLoss220Direct !== "" && Number(lineLoss220Direct) > 0
            ? { label: "220kV Line Loss", value: `${parseFloat(lineLoss220Direct).toFixed(2)}%` }
            : ((res220 != null && res220 !== "" && Number(res220) > 0) || (dist220 != null && dist220 !== "" && Number(dist220) > 0))
                ? { label: "220kV Line Impedance", value: `${parseFloat(res220 || 0).toFixed(4)} Ω / ${parseFloat(dist220 || 0).toFixed(2)} km` }
                : null,
        lineLoss400Direct != null && lineLoss400Direct !== "" && Number(lineLoss400Direct) > 0
            ? { label: "400kV Line Loss", value: `${parseFloat(lineLoss400Direct).toFixed(2)}%` }
            : ((res400 != null && res400 !== "" && Number(res400) > 0) || (dist400 != null && dist400 !== "" && Number(dist400) > 0))
                ? { label: "400kV Line Impedance", value: `${parseFloat(res400 || 0).toFixed(4)} Ω / ${parseFloat(dist400 || 0).toFixed(2)} km` }
                : null,
    ].filter(Boolean);



    // Prepare graph charts year label mapping
    const chartData = yearlyData.map(r => ({
        ...r,
        name: r.year.toString(),
        calendarYear: startYear + r.year - 1,
        solar_gen_33kv_mw: r.solar_gen_33kv_mw || 0,
        wind_gen_33kv_mw: r.wind_gen_33kv_mw || 0,
        solar_to_bess_power: r.solar_to_bess_power || 0,
        solar_power_at_poi: r.solar_power_at_poi ?? r.export_solar_power ?? 0,
        grid_import_at_poi: r.grid_import_at_poi ?? r.grid_import_at_400kv_bus ?? r.grid_import_for_bess_charging ?? 0,
        bess_power_at_poi: r.bess_power_at_poi ?? r.bess_power_at_400kv_bus ?? r.bess_power_at_220kv_bus ?? 0,
    }));

    return (
        <div className="bess-container-redesign min-h-screen pt-2 px-6 pb-6 md:pt-4 md:px-8 md:pb-8 flex flex-col">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,100..900;1,100..900&display=swap');

                .bess-container-redesign {
                    background-color: #f7f9fb !important;
                    font-family: 'Inter', sans-serif !important;
                    color: #191c1e !important;
                }

                .bess-container-redesign button,
                .bess-container-redesign input,
                .bess-container-redesign select,
                .bess-container-redesign textarea {
                    font-family: 'Inter', sans-serif !important;
                }

                .bess-container-redesign h1,
                .bess-container-redesign h2,
                .bess-container-redesign h3,
                .bess-container-redesign h4 {
                    font-family: 'Inter', sans-serif !important;        
                    font-weight: 600 !important;
                    border-left: none !important;
                    padding-left: 0 !important;
                    text-transform: none !important;
                    margin: 0 !important;
                    letter-spacing: -0.01em !important;
                }

                .bess-container-redesign .headline-lg {
                    font-size: 30px !important;
                    line-height: 38px !important;
                    letter-spacing: -0.02em !important;
                }

                .bess-card {
                    background-color: #ffffff !important;
                    border-radius: 10px !important;
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02) !important;
                    padding: 32px !important;
                    border: 1px solid #eceef0 !important;
                    box-sizing: border-box !important;
                }

                .bess-table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                }

                .bess-table th {
                    background-color: #f8fafc !important;
                    font-family: 'Inter', sans-serif !important;
                    font-size: 11px !important;
                    font-weight: 500 !important;
                    color: #707881 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 10px 16px !important;
                    border-bottom: 1px solid #e6e8ea !important;
                    position: sticky !important;
                    top: 0 !important;
                    z-index: 10 !important;
                }

                .bess-table td {
                    padding: 12px 16px !important;
                    border-bottom: 1px solid #f2f4f6 !important;
                    font-size: 13.5px !important;
                    color: #191c1e !important;
                }
            `}</style>

            <div className="max-w-[1280px] mx-auto w-full flex-1">
                {/* Header Back & Title Block */}
                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-[#bfc7d2] pb-4 gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate(resolvedBackPath)}
                            className="h-9 px-4 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                        >
                            <ArrowLeftOutlined /> Back
                        </button>
                        <h1 className="headline-lg font-bold text-[#0c2340]">
                            {hybridSimName}
                        </h1>
                        <span className="text-xs font-semibold text-slate-500 px-2.5 py-0.5 rounded bg-slate-100">{typeLabel} Results</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {config?.bess_sizing_simulation && (
                            <button
                                type="button"
                                onClick={() => window.open(`/bess-sizing/${config.bess_sizing_simulation}`, '_blank')}
                                className="h-9 px-3.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                                title="Open the underlying BESS Sizing Simulation report in a new tab"
                            >
                                <ExportOutlined /> BESS Report
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleOpenDownloadModal}
                            disabled={downloadingCSV}
                            className="h-9 px-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Download Yearly CSV Report"
                        >
                            {downloadingCSV ? <LoadingOutlined /> : <FileExcelOutlined />} Yearly Report
                        </button>
                        <button
                            type="button"
                            onClick={handleOpenMonthlyModal}
                            disabled={downloadingMonthlyCSV}
                            className="h-9 px-3.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Download Monthly CSV Report"
                        >
                            {downloadingMonthlyCSV ? <LoadingOutlined /> : <FileExcelOutlined />} Monthly Report
                        </button>
                        <button
                            type="button"
                            onClick={handleDownloadReport}
                            disabled={generatingReport}
                            className="h-9 px-3.5 bg-[#0052cc] hover:bg-[#0043a4] text-white rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {generatingReport ? <LoadingOutlined /> : <DownloadOutlined />} Download Report
                        </button>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Summary Parameters Card */}
                    <div className="border border-slate-200 rounded-2xl p-5 bg-white shadow-sm space-y-4">
                        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                            <h4 className="font-bold text-[#0052CC] text-xs uppercase tracking-wider">
                                Simulation Configurations Overview
                            </h4>
                            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">
                                {typeLabel}
                            </span>
                        </div>

                        {/* Primary Parameters Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-xs bg-[#f8fafc] border border-slate-100 rounded-xl p-3.5">
                            <div className="flex flex-col gap-0.5">
                                <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Simulation Type</span>
                                <span className="font-bold text-slate-800">{typeLabel}</span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Simulation Tenure</span>
                                <span className="font-bold text-slate-800">{config.simulation_tenure} Years</span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Sizing Sim Reference</span>
                                <span className="font-bold text-slate-800 truncate" title={sizingSimLabel}>{sizingSimLabel}</span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Evacuation Capacity</span>
                                <span className="font-bold text-slate-800">{parseFloat(config.evacuation_capacity_mva || 0).toFixed(1)} MVA</span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">BESS Feeder Capacity</span>
                                <span className="font-bold text-slate-800">{parseFloat(config.bess_feeder_capacity_mva || 0).toFixed(1)} MVA</span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-slate-400 font-semibold uppercase text-[9px] tracking-wide">Min BESS Stored Energy</span>
                                <span className="font-bold text-slate-800">
                                    {parseFloat(config.min_bess_store_energy || config.minBessStoreEnergy || 0).toFixed(2)}%
                                </span>
                            </div>
                        </div>

                        {/* System Losses Summary Section */}
                        <div className="pt-1">
                            <div className="flex justify-between items-center mb-2.5">
                                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                                    System Losses & Availability Summary
                                </span>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 text-xs">
                                {systemLossCards.map((card, idx) => (
                                    <div key={idx} className="p-2.5 border border-slate-100 rounded-xl bg-slate-50/60 flex flex-col gap-0.5">
                                        <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">{card.label}</span>
                                        <span className="font-bold text-slate-800">{card.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Interactive Min BESS Stored Energy Targets Panel */}
                    <div className="border border-slate-200 rounded-2xl p-5 bg-white shadow-sm space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-3">
                            <div>
                                <h4 className="font-bold text-[#0052CC] text-xs uppercase tracking-wider">
                                    Interactive Min BESS Stored Energy Targets
                                </h4>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                    Edit year-wise minimum BESS stored energy target percentages below to re-run and compare updated simulation results.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleUpdateMinBessAndReRun}
                                disabled={updatingMinBess || config?.is_frozen}
                                className="h-9 px-4 bg-[#0052cc] hover:bg-[#0043a4] text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed self-start sm:self-auto"
                                title={config?.is_frozen ? "Simulation is frozen" : ""}
                            >
                                {updatingMinBess ? <LoadingOutlined /> : <SyncOutlined />} Re-run Simulation
                            </button>
                        </div>

                        {/* Quick Bulk Apply Row */}
                        <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-100 text-xs">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-600 text-[11px]">Set Target for All Years:</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    placeholder="e.g. 100"
                                    value={bulkMinBessVal}
                                    disabled={config?.is_frozen}
                                    onChange={(e) => setBulkMinBessVal(e.target.value)}
                                    className="h-8 w-24 px-2.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-blue-500 bg-white"
                                />
                                <button
                                    type="button"
                                    disabled={config?.is_frozen || bulkMinBessVal === ""}
                                    onClick={() => {
                                        handleBulkApplyMinBess(bulkMinBessVal);
                                        setBulkMinBessVal("");
                                    }}
                                    className="h-8 px-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                                >
                                    Apply All
                                </button>
                            </div>
                            <span className="text-[11px] text-slate-400 font-medium">
                                Total Tenure: {editableYearlyMinBess.length} Years
                            </span>
                        </div>

                        {/* Year-by-year Grid/Table */}
                        <div className="overflow-x-auto max-h-[320px] border border-slate-100 rounded-xl">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider z-10">
                                    <tr>
                                        <th className="py-2.5 px-4">Project Year</th>
                                        <th className="py-2.5 px-4">Calendar Year</th>
                                        <th className="py-2.5 px-4">Min BESS Stored Energy Target (%)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {editableYearlyMinBess.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="py-2 px-4 font-bold text-slate-700">
                                                Year {item.year}
                                            </td>
                                            <td className="py-2 px-4 text-slate-500 geist-val font-medium">
                                                {startYear + item.year - 1}
                                            </td>
                                            <td className="py-2 px-4">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        value={item.pct ?? ""}
                                                        disabled={config?.is_frozen}
                                                        onChange={(e) => {
                                                            const val = e.target.value === "" ? "" : Math.min(100, Math.max(0, Number(e.target.value)));
                                                            const next = [...editableYearlyMinBess];
                                                            next[idx] = { ...next[idx], pct: val };
                                                            setEditableYearlyMinBess(next);
                                                        }}
                                                        className="h-8 w-28 px-2.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-blue-500 bg-white disabled:bg-slate-100 disabled:text-slate-400"
                                                    />
                                                    <span className="text-slate-400 font-semibold text-[11px]">%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Simulation Results Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h4 className="font-bold text-slate-800 text-sm">Generation & Dispatch Trends</h4>
                                    <p className="text-xs text-slate-400 mt-0.5">Chronological overview of generation, charging, and POI dispatch trends.</p>
                                </div>
                                <div className="min-w-[220px] max-w-[340px]">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        placeholder="Select fields to graph"
                                        value={selectedTrendFields}
                                        onChange={(vals) => setSelectedTrendFields(vals)}
                                        options={currentTrendFieldConfig.map(f => ({ label: f.label, value: f.key }))}
                                        style={{ width: "100%" }}
                                        maxTagCount="responsive"
                                        size="small"
                                    />
                                </div>
                            </div>
                            <div className="h-72 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="name" stroke="#94a3b8" fontSize={9} interval="preserveStartEnd" />
                                        <YAxis stroke="#94a3b8" fontSize={9} />
                                        <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #f1f5f9" }} formatter={(v, name) => [parseFloat(v).toFixed(1), name]} labelFormatter={(label) => { const row = chartData.find(item => item.name === label); return row ? row.calendarYear.toString() : label; }} />
                                        <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: 10 }} />
                                        {currentTrendFieldConfig.filter(f => selectedTrendFields.includes(f.key)).map(f => (
                                            <Line
                                                key={f.key}
                                                type="monotone"
                                                dataKey={f.key}
                                                name={f.label}
                                                stroke={f.color}
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h4 className="font-bold text-slate-800 text-sm">BESS Charging Breakdown</h4>
                                    <p className="text-xs text-slate-400 mt-0.5">Stacked breakdown of solar generation, charging, and POI power flows.</p>
                                </div>
                                <div className="min-w-[220px] max-w-[340px]">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        placeholder="Select fields to graph"
                                        value={selectedChargingFields}
                                        onChange={(vals) => setSelectedChargingFields(vals)}
                                        options={currentTrendFieldConfig.map(f => ({ label: f.label, value: f.key }))}
                                        style={{ width: "100%" }}
                                        maxTagCount="responsive"
                                        size="small"
                                    />
                                </div>
                            </div>
                            <div className="h-72 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="name" stroke="#94a3b8" fontSize={9} />
                                        <YAxis stroke="#94a3b8" fontSize={9} />
                                        <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #f1f5f9" }} formatter={(v, name) => [parseFloat(v).toFixed(1), name]} labelFormatter={(label) => { const row = chartData.find(item => item.name === label); return row ? row.calendarYear.toString() : label; }} />
                                        <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: 10 }} />
                                        {currentTrendFieldConfig.filter(f => selectedChargingFields.includes(f.key)).map(f => (
                                            <Bar
                                                key={f.key}
                                                dataKey={f.key}
                                                name={f.label}
                                                fill={f.color}
                                                stackId="charging"
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* BESS Capacity & Fleet Growth */}
                    <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">
                        <div>
                            <h4 className="font-bold text-slate-800 text-sm">BESS Capacity & Container Sizing Profile</h4>
                            <p className="text-xs text-slate-400 mt-0.5">Chronological battery storage capacity curve and total container quantities.</p>
                        </div>
                        <div className="h-72 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={9} />
                                    <YAxis stroke="#94a3b8" fontSize={9} />
                                    <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #f1f5f9" }} formatter={(v, name) => [parseFloat(v).toFixed(1), name]} labelFormatter={(label) => { const row = chartData.find(item => item.name === label); return row ? row.calendarYear.toString() : label; }} />
                                    <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: 10 }} />
                                    <defs>
                                        <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <Area type="monotone" dataKey="bess_capacity" name="BESS Capacity (MWh)" stroke="#4f46e5" fillOpacity={1} fill="url(#capGrad)" strokeWidth={2} />
                                    <Line type="monotone" dataKey="total_container_no" name="Container Qty" stroke="#ec4899" strokeWidth={2} dot={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>



                    {/* Detailed Simulation Table */}
                    <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h4 className="font-bold text-slate-800 text-sm">Yearly Summary Table</h4>
                                <p className="text-xs text-slate-400 mt-0.5">Complete year-by-year summary of all energy inputs, losses, charging records, and dispatch power.</p>
                            </div>
                        </div>
                        <div className="w-full border border-slate-100 rounded-xl overflow-auto max-h-[500px]">
                            <table className="w-full text-left border-collapse min-w-[1000px] bess-table text-xs">
                                <thead>
                                    <tr>
                                        <th>Year</th>
                                        <th className="text-right">Solar Gen (MWh)</th>
                                        <th className="text-right">Solar Exported (MWh)</th>
                                        <th className="text-right">Solar to BESS (MWh)</th>
                                        <th className="text-right">Grid Import (MWh)</th>
                                        <th className="text-right">BESS POI Discharge (MWh)</th>
                                        <th className="text-right">BESS Capacity (MWh)</th>
                                        <th className="text-center">Containers</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-slate-600">
                                    {yearlyData.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                                            <td className="font-semibold text-slate-800">Yr {row.year}</td>
                                            <td className="text-right font-medium">{(row.solar_gen_33kv_mw || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                            <td className="text-right font-medium">{(row.export_solar_power || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                            <td className="text-right font-medium">{(row.solar_to_bess_power || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                            <td className="text-right font-medium">{(row.grid_import_for_bess_charging || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                            <td className="text-right font-semibold text-slate-800">{(row.bess_power_at_400kv_bus || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                            <td className="text-right font-semibold text-slate-800">{(row.bess_capacity || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                            <td className="text-center font-bold text-slate-700">{row.total_container_no}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hidden Printable Area for A4 PDF Report Generation */}
            <div id="hybrid-report-printable-area" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
                <style>{`
                    .bess-report-page {
                        background-color: #ffffff;
                        width: 794px;
                        height: 1123px;
                        padding: 50px 60px;
                        box-sizing: border-box;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        font-family: 'Inter', sans-serif;
                        color: #1e293b;
                    }
                    .bess-report-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        border-bottom: 2px solid #0052cc;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    .bess-report-title {
                        font-family: 'Inter', sans-serif;
                        font-size: 24px;
                        font-weight: 800;
                        color: #0c2340;
                        margin: 0;
                    }
                    .bess-report-section {
                        margin-bottom: 24px;
                    }
                    .bess-report-section-title {
                        font-family: 'Inter', sans-serif;
                        font-size: 14px;
                        font-weight: 700;
                        color: #0052cc;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        border-bottom: 1px solid #e2e8f0;
                        padding-bottom: 6px;
                        margin-bottom: 14px;
                    }
                    .bess-report-grid {
                        display: grid;
                        grid-template-cols: repeat(2, 1fr);
                        gap: 12px 24px;
                        font-size: 11px;
                    }
                    .bess-report-item {
                        display: flex;
                        justify-content: space-between;
                        border-bottom: 1px dashed #f1f5f9;
                        padding-bottom: 4px;
                    }
                    .bess-report-label {
                        color: #64748b;
                        font-weight: 500;
                    }
                    .bess-report-value {
                        color: #0f172a;
                        font-weight: 700;
                    }
                    .bess-report-footer {
                        border-top: 1px solid #e2e8f0;
                        padding-top: 10px;
                        display: flex;
                        justify-content: space-between;
                        font-size: 9px;
                        color: #94a3b8;
                        font-weight: 500;
                    }
                `}</style>

                {/* Report Page 1: Input Summary */}
                <div id="hybrid-report-page-1" className="bess-report-page">
                    <div>
                        <div className="bess-report-header">
                            <h2 className="bess-report-title">Hybrid BESS Simulation Report</h2>
                            <span style={{ fontSize: 10, fontWeight: "bold", color: "#64748b" }}>ID: #{id}</span>
                        </div>

                        <div className="bess-report-section" style={{ marginTop: 10 }}>
                            <div className="bess-report-section-title">Run configuration Overview</div>
                            <div className="bess-report-grid">
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Simulation ID</span>
                                    <span className="bess-report-value">#{id}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Simulation Type</span>
                                    <span className="bess-report-value">{typeLabel}</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Simulation Tenure</span>
                                    <span className="bess-report-value">{config.simulation_tenure} Years</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">BESS Sizing Reference</span>
                                    <span className="bess-report-value">{sizingSimLabel}</span>
                                </div>
                                {isWindSim && (
                                    <div className="bess-report-item">
                                        <span className="bess-report-label">Battery Degradation</span>
                                        <span className="bess-report-value">{config.bess_degradation === 2 ? "At Factor" : "At Actual"}</span>
                                    </div>
                                )}
                                {isWindSim && config.bess_degradation !== 2 && config.adjusting_factor != null && (
                                    <div className="bess-report-item">
                                        <span className="bess-report-label">Adjusting Factor</span>
                                        <span className="bess-report-value">{Number(config.adjusting_factor).toFixed(4)}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bess-report-section">
                            <div className="bess-report-section-title">System Losses & Unavailability</div>
                            <div className="bess-report-grid">
                                {systemLossCards.map((card, idx) => (
                                    <div key={idx} className="bess-report-item">
                                        <span className="bess-report-label">{card.label}</span>
                                        <span className="bess-report-value">{card.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bess-report-section">
                            <div className="bess-report-section-title">Evacuation & Feeder Capacities</div>
                            <div className="bess-report-grid">
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Evacuation Capacity</span>
                                    <span className="bess-report-value">{parseFloat(config.evacuation_capacity_mva || 0).toFixed(1)} MVA</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">BESS Feeder Capacity</span>
                                    <span className="bess-report-value">{parseFloat(config.bess_feeder_capacity_mva || 0).toFixed(1)} MVA</span>
                                </div>
                                <div className="bess-report-item">
                                    <span className="bess-report-label">Solar Feeder Capacity</span>
                                    <span className="bess-report-value">{parseFloat(config.solar_feeder_capacity_mva || 0).toFixed(1)} MVA</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy Hybrid BESS Sizing Module</span>
                        <span>Page 1 of 3</span>
                    </div>
                </div>

                {/* Report Page 2: Summary Outputs Table */}
                <div id="hybrid-report-page-2" className="bess-report-page" style={{ padding: "40px 50px" }}>
                    <div>
                        <div className="bess-report-header" style={{ marginBottom: "10px" }}>
                            <h2 className="bess-report-title">Yearly Summary Table</h2>
                        </div>

                        <div className="bess-report-section" style={{ marginBottom: "10px" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "8px", textAlign: "center", fontFamily: "Inter, sans-serif" }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#f8fafc" }}>
                                        <th style={{ border: "1px solid #cbd5e1", padding: "4px 5px", fontWeight: "bold" }}>Year</th>
                                        <th style={{ border: "1px solid #cbd5e1", padding: "4px 5px", fontWeight: "bold" }}>Solar Gen (MWh)</th>
                                        <th style={{ border: "1px solid #cbd5e1", padding: "4px 5px", fontWeight: "bold" }}>Solar Exported (MWh)</th>
                                        <th style={{ border: "1px solid #cbd5e1", padding: "4px 5px", fontWeight: "bold" }}>BESS Dispatch (MWh)</th>
                                        <th style={{ border: "1px solid #cbd5e1", padding: "4px 5px", fontWeight: "bold" }}>Grid Import (MWh)</th>
                                        <th style={{ border: "1px solid #cbd5e1", padding: "4px 5px", fontWeight: "bold" }}>BESS Cap (MWh)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {yearlyData.map((row, idx) => (
                                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa" }}>
                                            <td style={{ border: "1px solid #cbd5e1", padding: "3px 4px", fontWeight: "bold" }}>Yr {row.year}</td>
                                            <td style={{ border: "1px solid #cbd5e1", padding: "3px 4px" }}>{Math.round(row.solar_gen_33kv_mw || 0).toLocaleString()}</td>
                                            <td style={{ border: "1px solid #cbd5e1", padding: "3px 4px" }}>{Math.round(row.export_solar_power || 0).toLocaleString()}</td>
                                            <td style={{ border: "1px solid #cbd5e1", padding: "3px 4px" }}>{Math.round(row.bess_power_at_400kv_bus || 0).toLocaleString()}</td>
                                            <td style={{ border: "1px solid #cbd5e1", padding: "3px 4px" }}>{Math.round(row.grid_import_for_bess_charging || 0).toLocaleString()}</td>
                                            <td style={{ border: "1px solid #cbd5e1", padding: "3px 4px" }}>{Math.round(row.bess_capacity || 0).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy Hybrid BESS Sizing Module</span>
                        <span>Page 2 of 3</span>
                    </div>
                </div>

                {/* Report Page 3: Generation & Capacity Charts */}
                <div id="hybrid-report-page-3" className="bess-report-page" style={{ padding: "40px 50px" }}>
                    <div>
                        <div className="bess-report-header" style={{ marginBottom: "15px" }}>
                            <h2 className="bess-report-title">Simulations Trends Graphs</h2>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
                            <div style={{ border: "1px solid #e2e8f0", borderRadius: "6px", padding: "10px", backgroundColor: "#ffffff" }}>
                                <h4 style={{ fontSize: "10px", margin: "0 0 5px 0", color: "#1e293b", fontWeight: "bold" }}>Generation & Dispatch Trends</h4>
                                <div style={{ height: "180px", width: "650px" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis dataKey="name" fontSize={7} />
                                            <YAxis fontSize={7} />
                                            <Legend verticalAlign="top" height={20} wrapperStyle={{ fontSize: 8 }} />
                                            {currentTrendFieldConfig.filter(f => selectedTrendFields.includes(f.key)).map(f => (
                                                <Line
                                                    key={f.key}
                                                    type="monotone"
                                                    dataKey={f.key}
                                                    name={f.label}
                                                    stroke={f.color}
                                                    strokeWidth={1.5}
                                                    dot={false}
                                                />
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div style={{ border: "1px solid #e2e8f0", borderRadius: "6px", padding: "10px", backgroundColor: "#ffffff" }}>
                                <h4 style={{ fontSize: "10px", margin: "0 0 5px 0", color: "#1e293b", fontWeight: "bold" }}>BESS Sizing Profile</h4>
                                <div style={{ height: "180px", width: "650px" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis dataKey="name" fontSize={7} />
                                            <YAxis fontSize={7} />
                                            <Legend verticalAlign="top" height={20} wrapperStyle={{ fontSize: 8 }} />
                                            <Area type="monotone" dataKey="bess_capacity" name="Capacity (MWh)" stroke="#4f46e5" fill="#e0e7ff" strokeWidth={1.5} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bess-report-footer">
                        <span>Apraava Energy Hybrid BESS Sizing Module</span>
                        <span>Page 3 of 3</span>
                    </div>
                </div>
            </div>
            
            {/* Hidden BESS Sizing Component for PDF generation */}
            {config?.bess_sizing_simulation && (
                <BessSizingResultPage hiddenReportOnlyMode={true} simulationId={config.bess_sizing_simulation} />
            )}

            <Modal
                title="Download Yearly Report"
                open={downloadModalVisible}
                onOk={handleDownloadCSV}
                onCancel={() => setDownloadModalVisible(false)}
                okText="Download"
                cancelText="Cancel"
            >
                <div className="flex flex-col gap-4 py-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-slate-700">Select Year(s)</label>
                        <Select
                            value={downloadOption}
                            onChange={(val) => setDownloadOption(val)}
                            className="w-full"
                            options={[
                                { value: "all", label: "All Years" },
                                ...Array.from({ length: config?.simulation_tenure || 25 }, (_, i) => ({
                                    value: String(i + 1),
                                    label: `Year ${i + 1}`
                                }))
                            ]}
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                title="Download Monthly Report"
                open={monthlyModalVisible}
                onOk={handleDownloadMonthlyCSV}
                onCancel={() => setMonthlyModalVisible(false)}
                okText="Download"
                cancelText="Cancel"
            >
                <div className="flex flex-col gap-4 py-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-slate-700">Select Year</label>
                        <Select
                            value={monthlyYear}
                            onChange={(val) => setMonthlyYear(val)}
                            className="w-full"
                            options={Array.from({ length: config?.simulation_tenure || 25 }, (_, i) => ({
                                value: String(i + 1),
                                label: `Year ${i + 1}`
                            }))}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-slate-700">Select Month</label>
                        <Select
                            value={monthlyMonth}
                            onChange={(val) => setMonthlyMonth(val)}
                            className="w-full"
                            options={MONTH_OPTIONS}
                        />
                    </div>
                </div>
            </Modal>

        </div>
    );
}
